import { useMemo, useCallback, useRef } from 'react';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { Utils } from '@frontend/task-center-components';
import { LocationsData } from '@frontend/task-center-scope';
import { useForm, shallowEqual } from '@frontend/design-system';
import { CreateTaskFormKeys, CreateTaskFormType } from '../types';
import { getFulfilledValues } from '../utils';

interface UseGetFormDetailsProps {
  locationIds: string[];
  selectedTask: TaskCenterTypes.Task | null;
  locationsData: LocationsData[];
}

export const useGetFormDetails = ({ locationIds, selectedTask, locationsData }: UseGetFormDetailsProps) => {
  const { t } = useTranslation('task-center');
  const timezone = Utils.getLocationTimeZone(locationsData, selectedTask?.locationId);

  const formProps = useForm<CreateTaskFormType, CreateTaskFormKeys>({
    fields: {
      title: {
        type: 'text',
        required: true,
        placeholder: t('Task Title...'),
        value: selectedTask?.title ?? '',
      },
      description: {
        type: 'text',
        placeholder: t('Add Description...'),
        value: selectedTask?.description ?? '',
      },
      locationId: {
        type: 'dropdown',
        placeholder: t('Select Location'),
        value: selectedTask?.locationId ? selectedTask.locationId : locationIds.length > 1 ? '' : locationIds[0],
        required: true,
      },
      patient: {
        type: 'dropdown',
        placeholder: t('Select Patient'),
        value: selectedTask?.patient?.id ?? '',
      },
      dueDate: {
        type: 'text',
        placeholder: t('Pick Due Date'),
        value: Utils.convertUTCToLocal({
          date: selectedTask?.dueDate,
          tz: timezone,
          hasTime: selectedTask?.dueDateHasTime,
        }),
      },
      assignee: {
        type: 'dropdown',
        placeholder: t('Select Assignee'),
        value: selectedTask?.assignee ?? '',
      },
      status: {
        type: 'dropdown',
        placeholder: t('Select Status'),
        required: true,
        value: selectedTask?.status ?? TaskCenterTypes.TaskStatus.STATUS_TODO,
      },
      priority: {
        type: 'dropdown',
        placeholder: t('No Priority'),
        value: selectedTask?.priority ?? TaskCenterTypes.PriorityType.PRIORITY_UNKNOWN,
      },
    },
  });

  const initialValues = useRef(formProps.values);

  const formValues = useMemo(() => getFulfilledValues(formProps.values), [formProps.values]);

  /* This function is responsible for generating values depending on the selectedTask value.
     if selectedTask has no value that means we are trying to create a task, and we return the whole object as is
     else if there is task selected then we compare the form values with the values of the task
     and return only the values that are different
  */
  const getUpdatedValues = useCallback(() => {
    if (selectedTask) {
      const updatedValues: Partial<Omit<TaskCenterTypes.Task, 'patient'> & { patient?: string }> = {};
      const values = formProps.values;
      Object.keys(values as Array<keyof CreateTaskFormType>).forEach((key) => {
        if (key === 'patient') {
          if (values[key] !== selectedTask.patient?.id && (selectedTask.patient?.id || values[key])) {
            (updatedValues as any)[key] = values[key];
          }
        } else if (key === 'dueDate') {
          const dateWithTimeZone = Utils.convertUTCToLocal({
            date: selectedTask?.dueDate,
            tz: timezone,
            hasTime: selectedTask?.dueDateHasTime,
          });
          if (values[key] !== dateWithTimeZone) {
            (updatedValues as any)[key] = values[key];
          }
        } else if (
          values[key as keyof CreateTaskFormType] !== selectedTask[key as keyof TaskCenterTypes.Task] &&
          (values[key as keyof CreateTaskFormType] || selectedTask[key as keyof TaskCenterTypes.Task])
        ) {
          // https://linear.app/getweave/issue/BIZX-721/updated-taskcenter-create-task-form-types
          // This values of from and selectedTask are not compatible with each other, it will be fixed with the
          // above ticket. Hence bypassing it with any as of now
          // TODO:
          (updatedValues as any)[key] = values[key as keyof CreateTaskFormType];
        }
      });
      return updatedValues;
    }
    return formValues;
  }, [formProps.values, formValues, selectedTask, timezone]);

  return {
    formProps,
    formValues,
    getUpdatedValues,
    haveValuesChanged: !shallowEqual(initialValues.current, formProps.values),
  };
};
